// ===== 1. Imports =====
import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import { db } from "../../firebaseConfig";
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { FileText, AlertTriangle } from "lucide-react";
import { fmt } from "./utils/formatters";

// ===== 2. Componente =====
export default function InvoiceModal({
  projectCode,
  editingInvoice,
  setEditingInvoice,
  userCanCompleteInvoice,
}) {
  const [poItems, setPoItems] = useState([]);
  const [allocations, setAllocations] = useState({});

  if (!editingInvoice) return null;

  // ===== 3. Cargar items de la PO =====
  useEffect(() => {
    const fetchItems = async () => {
      if (!editingInvoice._poId) return;
      const itemsRef = collection(
        db,
        "projects",
        projectCode,
        "pos",
        editingInvoice._poId,
        "items"
      );
      const snap = await getDocs(itemsRef);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPoItems(items);

      // Preasignar si la factura ya tiene distribución
      if (editingInvoice.allocations)
        setAllocations(editingInvoice.allocations);
    };
    fetchItems();
  }, [editingInvoice?._poId, projectCode]);

  // ===== 4. Totales =====
  const baseAssigned = Object.values(allocations).reduce(
    (a, b) => a + (Number(b) || 0),
    0
  );
  const tax = Number(editingInvoice.taxPercent) || 0;
  const irpf = Number(editingInvoice.irpfPercent) || 0;
  const totalAmount = baseAssigned * (1 + tax / 100 - irpf / 100);

  // ===== 5. Cambiar asignación =====
  const handleAllocChange = (id, value) => {
    setAllocations((prev) => ({ ...prev, [id]: value }));
  };

  // ===== 6. Guardar cambios =====
  const handleSave = async () => {
    const data = {
      invoiceNumber: editingInvoice.invoiceNumber || "",
      supplier: editingInvoice.supplier || "",
      description: editingInvoice.description || "",
      dueDate: editingInvoice.dueDate || "",
      taxPercent: tax,
      irpfPercent: irpf,
      baseAmount: baseAssigned,
      totalAmount,
      allocations,
      coded: true,
      modifiedAt: serverTimestamp(),
    };

    try {
      const invRef = doc(
        db,
        "projects",
        projectCode,
        "pos",
        editingInvoice._poId,
        "invoices",
        editingInvoice.id
      );
      await updateDoc(invRef, data);
      alert("Factura actualizada correctamente");
      setEditingInvoice(null);
    } catch (err) {
      console.error("Error al guardar factura:", err);
      alert("Error al actualizar factura. Revisa la consola.");
    }
  };

  // ===== 7. Render =====
  return (
    <Modal
      title={`Factura ${editingInvoice.invoiceNumber || editingInvoice.id}`}
      onClose={() => setEditingInvoice(null)}
      className="modal--large"
      style={{ maxWidth: "1000px", maxHeight: "90vh", overflowY: "auto" }}
    >
      <div className="stack">
        {/* --- Info básica --- */}
        <div className="form-grid">
          <div className="field">
            <label>ID</label>
            <input value={editingInvoice.id} readOnly />
          </div>
          <div className="field">
            <label>PO vinculada</label>
            <input value={editingInvoice.poNumber} readOnly />
          </div>
          <div className="field">
            <label>Vencimiento</label>
            <input
              type="date"
              value={editingInvoice.dueDate || ""}
              onChange={(e) =>
                setEditingInvoice((inv) => ({
                  ...inv,
                  dueDate: e.target.value,
                }))
              }
              disabled={!userCanCompleteInvoice}
            />
          </div>
          <div className="field">
            <label>Archivo</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FileText size={16} />
              <a href={editingInvoice.fileUrl} target="_blank" rel="noreferrer">
                {editingInvoice.fileName || "(archivo)"}
              </a>
            </div>
          </div>
        </div>

        {/* --- Info contable --- */}
        <h3 style={{ marginTop: 8 }}>Información contable</h3>
        <div className="form-grid">
          <div className="field">
            <label>N.º factura</label>
            <input
              type="text"
              value={editingInvoice.invoiceNumber || ""}
              onChange={(e) =>
                setEditingInvoice((inv) => ({
                  ...inv,
                  invoiceNumber: e.target.value,
                }))
              }
              disabled={!userCanCompleteInvoice}
            />
          </div>

          <div className="field">
            <label>Proveedor</label>
            <input
              type="text"
              value={editingInvoice.supplier || ""}
              onChange={(e) =>
                setEditingInvoice((inv) => ({
                  ...inv,
                  supplier: e.target.value,
                }))
              }
              disabled={!userCanCompleteInvoice}
            />
          </div>

          <div className="field field--span2">
            <label>Descripción</label>
            <input
              type="text"
              value={editingInvoice.description || ""}
              onChange={(e) =>
                setEditingInvoice((inv) => ({
                  ...inv,
                  description: e.target.value,
                }))
              }
              disabled={!userCanCompleteInvoice}
            />
          </div>

          <div className="field">
            <label>IVA (%)</label>
            <input
              type="number"
              value={editingInvoice.taxPercent || 0}
              onChange={(e) =>
                setEditingInvoice((inv) => ({
                  ...inv,
                  taxPercent: Number(e.target.value) || 0,
                }))
              }
              disabled={!userCanCompleteInvoice}
            />
          </div>

          <div className="field">
            <label>IRPF (%)</label>
            <input
              type="number"
              value={editingInvoice.irpfPercent || 0}
              onChange={(e) =>
                setEditingInvoice((inv) => ({
                  ...inv,
                  irpfPercent: Number(e.target.value) || 0,
                }))
              }
              disabled={!userCanCompleteInvoice}
            />
          </div>
        </div>

        {/* --- Asignación de ítems --- */}
        <h3 style={{ marginTop: 16 }}>Asignación a ítems de la PO</h3>
        {poItems.length === 0 ? (
          <div style={{ color: "var(--muted)" }}>
            No se encontraron ítems en esta PO.
          </div>
        ) : (
          <div
            className="card"
            style={{
              padding: 12,
              border: "1px solid var(--ring)",
              borderRadius: 8,
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {poItems.map((item) => {
              const allocated = Number(allocations[item.id]) || 0;
              const baseTotal = Number(item.baseTotal) || 0;
              const invoiced = Number(item.baseInvoiced) || 0;
              const available = baseTotal - invoiced;

              const over = allocated > available;

              return (
                <div
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 100px 100px 100px",
                    gap: 10,
                    alignItems: "center",
                    padding: "4px 0",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.description}</div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--muted-foreground)",
                      }}
                    >
                      Cuenta {item.accountCode} · IVA {item.vatType}%
                    </div>
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 13 }}>
                    Disp.: {fmt(available)} €
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={allocations[item.id] || ""}
                    onChange={(e) => handleAllocChange(item.id, e.target.value)}
                    disabled={!userCanCompleteInvoice}
                    style={{
                      borderColor: over ? "var(--danger)" : "var(--input)",
                      background: over ? "#fff5f5" : "inherit",
                      padding: "4px 6px",
                      width: "100%",
                    }}
                  />
                  {over && (
                    <AlertTriangle
                      size={14}
                      color="var(--danger)"
                      title="Importe asignado superior al disponible"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* --- Resumen --- */}
        <div
          className="card"
          style={{
            padding: 14,
            background: "var(--muted-background)",
            marginTop: 12,
          }}
        >
          <strong>Resumen:</strong>
          <div style={{ marginTop: 6, fontSize: 14 }}>
            Base asignada: {fmt(baseAssigned)} € <br />
            IVA ({tax}%): {fmt((baseAssigned * tax) / 100)} € <br />
            IRPF ({irpf}%): {fmt((baseAssigned * irpf) / 100)} € <br />
            <strong>Total factura: {fmt(totalAmount)} €</strong>
          </div>
        </div>

        {/* --- Botones --- */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button className="button" onClick={() => setEditingInvoice(null)}>
            Cerrar
          </button>
          {userCanCompleteInvoice && (
            <button className="button button--primary" onClick={handleSave}>
              Guardar cambios
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
